import type {
  FeedbackPayload,
  RemoteConfig,
  FeedbackSummary,
  FeedbackComment,
  MyFeedbackResponse,
} from './types';

/**
 * HTTP client for communicating with the FeedbackHub API.
 */
export class ApiClient {
  private baseUrl: string;
  private apiKey: string;

  constructor(apiUrl: string, apiKey: string) {
    // Normalise: strip trailing slashes, then ensure we end on /api/v1
    let base = apiUrl.replace(/\/+$/, '');
    // Strip legacy suffixes so we always rebuild a canonical base
    base = base.replace(/\/api(\/v\d+)?$/i, '');
    this.baseUrl = `${base}/api/v1`;
    this.apiKey = apiKey;
  }

  // ─── Remote Config ──────────────────────────────────────────────────

  /** Fetch widget configuration from the dashboard */
  async getConfig(): Promise<RemoteConfig> {
    const res = await this.request('GET', '/widget/config');
    return res.json();
  }

  // ─── Feedback Submission ────────────────────────────────────────────

  /**
   * Submit feedback with optional media attachment.
   * Returns the created feedback ID.
   */
  async submitFeedback(
    payload: FeedbackPayload,
    mediaBlob?: Blob,
    mediaType?: 'screenshot' | 'video',
  ): Promise<{ feedbackId: string }> {
    const formData = new FormData();
    formData.append('payload', JSON.stringify(payload));

    if (mediaBlob && mediaType) {
      const ext = mediaType === 'screenshot' ? 'png' : 'webm';
      const mimeType = mediaType === 'screenshot' ? 'image/png' : 'video/webm';
      formData.append('media', mediaBlob, `capture.${ext}`);
      formData.append('mediaType', mimeType);
    }

    const res = await this.request('POST', '/feedback', formData);
    return res.json();
  }

  /**
   * Check for potential duplicate feedback.
   * Returns list of similar items already reported for this app.
   */
  async checkDuplicates(
    title: string,
    description: string,
  ): Promise<
    Array<{
      id: string;
      title: string;
      similarity: number;
      createdAt: string;
      status: string;
    }>
  > {
    try {
      const params = new URLSearchParams({ title, description });
      const res = await this.request('GET', `/check-duplicates?${params}`);
      const data = await res.json();
      return data.potentialDuplicates || [];
    } catch {
      return []; // Fail silently — don't block feedback submission
    }
  }

  // ─── My Feedback (Communication Loop) ──────────────────────────────

  /** Get all feedback items submitted by this tester (by email) */
  async getMyFeedback(email: string): Promise<MyFeedbackResponse> {
    const res = await this.request(
      'GET',
      `/my-feedback?email=${encodeURIComponent(email)}`,
    );
    return res.json();
  }

  /** Get the comment thread for a specific feedback item */
  async getComments(feedbackId: string, testerToken: string, testerEmail?: string): Promise<FeedbackComment[]> {
    let url = `/feedback/${feedbackId}/comments?testerToken=${encodeURIComponent(testerToken)}`;
    if (testerEmail) url += `&testerEmail=${encodeURIComponent(testerEmail)}`;
    const res = await this.request('GET', url);
    return res.json();
  }

  async confirmVerification(
    feedbackId: string,
    itemIds: string[],
    testerToken: string,
    testerEmail?: string,
  ): Promise<{ id: string; verificationChecklist?: Array<{ id: string; step: string; context?: string; status: string; verifiedAt?: string; verifiedBy?: string }>; verificationStatus?: string }> {
    const res = await this.request(
      'PATCH',
      `/feedback/${feedbackId}`,
      JSON.stringify({
        confirmVerification: { itemIds, verifiedBy: testerEmail || 'widget-user' },
        testerToken,
        testerEmail,
      }),
      { 'Content-Type': 'application/json' },
    );
    return res.json();
  }

  async updateVerificationItems(
    feedbackId: string,
    items: Array<{ id: string; status: 'pending' | 'verified' | 'failed'; note?: string; evidenceUrl?: string }>,
    testerToken: string,
    testerEmail?: string,
  ): Promise<{ id: string; verificationChecklist?: Array<{ id: string; step: string; context?: string; status: string; note?: string; evidenceUrl?: string; verifiedAt?: string; verifiedBy?: string }>; verificationStatus?: string }> {
    const res = await this.request(
      'PATCH',
      `/feedback/${feedbackId}`,
      JSON.stringify({
        updateVerification: {
          items,
          verifiedBy: testerEmail || 'widget-user',
        },
        testerToken,
        testerEmail,
      }),
      { 'Content-Type': 'application/json' },
    );
    return res.json();
  }

  /** Post a reply comment as the tester */
  async postComment(
    feedbackId: string,
    testerToken: string,
    message: string,
    testerEmail?: string,
    media?: Blob,
  ): Promise<FeedbackComment> {
    if (media) {
      const formData = new FormData();
      formData.append('message', message);
      formData.append('author', 'tester');
      formData.append('testerToken', testerToken);
      if (testerEmail) formData.append('testerEmail', testerEmail);
      formData.append('media', media, media.type?.includes('video') ? 'capture.webm' : 'capture.png');
      const res = await this.request(
        'POST',
        `/feedback/${feedbackId}/comments`,
        formData,
      );
      return res.json();
    }
    const res = await this.request(
      'POST',
      `/feedback/${feedbackId}/comments`,
      JSON.stringify({ testerToken, message, author: 'tester', testerEmail }),
      { 'Content-Type': 'application/json' },
    );
    return res.json();
  }

  // ─── Conversational Bot ───────────────────────────────────────────

  async startBotSession(payload?: {
    testerEmail?: string;
    metadata?: Record<string, unknown>;
  }): Promise<{ sessionId: string; greeting: string }> {
    const res = await this.request(
      'POST',
      '/bot/start',
      JSON.stringify(payload ?? {}),
      { 'Content-Type': 'application/json' },
    );
    return res.json();
  }

  async attachBotCapture(
    sessionId: string,
    mediaBlob: Blob,
    mediaType: 'screenshot' | 'video',
  ): Promise<{ captureType: 'screenshot' | 'video'; captureUrl: string }> {
    const formData = new FormData();
    const ext = mediaType === 'screenshot' ? 'png' : 'webm';
    const mimeType = mediaType === 'screenshot' ? 'image/png' : 'video/webm';
    formData.append('sessionId', sessionId);
    formData.append('media', mediaBlob, `capture.${ext}`);
    formData.append('mediaType', mimeType);

    const res = await this.request('POST', '/bot/attach-capture', formData);
    return res.json();
  }

  async streamBotTurn(
    sessionId: string,
    userMessage: string,
    handlers: {
      onDelta?: (delta: string) => void;
      onReady?: () => void;
      onError?: (message: string) => void;
      onDone?: () => void;
    },
  ): Promise<void> {
    const res = await this.request(
      'POST',
      '/bot/turn',
      JSON.stringify({ sessionId, userMessage }),
      { 'Content-Type': 'application/json' },
    );

    if (!res.body) {
      handlers.onError?.('No stream body returned from bot turn endpoint.');
      return;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffered = '';

    const parseEvent = (line: string): void => {
      if (!line.startsWith('data:')) return;
      const raw = line.slice(5).trim();
      if (!raw) return;
      try {
        const payload = JSON.parse(raw) as {
          delta?: string;
          ready?: boolean;
          done?: boolean;
          error?: string;
        };
        if (payload.delta) handlers.onDelta?.(payload.delta);
        if (payload.ready) handlers.onReady?.();
        if (payload.error) handlers.onError?.(payload.error);
        if (payload.done) handlers.onDone?.();
      } catch {
        // Ignore malformed SSE data
      }
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        if (buffered.trim()) parseEvent(buffered.trim());
        break;
      }

      buffered += decoder.decode(value, { stream: true });
      const lines = buffered.split('\n');
      buffered = lines.pop() ?? '';

      for (const line of lines) {
        parseEvent(line.trim());
      }
    }
  }

  async finalizeBot(sessionId: string, diagnostics?: import('./types').DiagnosticsSnapshot, testerEmail?: string): Promise<{ feedbackId: string }> {
    const res = await this.request(
      'POST',
      '/bot/finalize',
      JSON.stringify({ sessionId, diagnostics, testerEmail }),
      { 'Content-Type': 'application/json' },
    );
    return res.json();
  }

  async resumeBotSession(sessionId: string): Promise<{
    sessionId: string;
    status: string;
    messages: Array<{
      role: 'user' | 'assistant';
      content: string;
      timestamp?: number;
      captures?: Array<{
        id: string;
        type: 'screenshot' | 'video';
        url: string;
      }>;
    }>;
    createdAt?: number;
    finalizedAt?: number | null;
  }> {
    const res = await this.request('GET', `/bot/resume/${encodeURIComponent(sessionId)}`);
    return res.json();
  }

  // ─── HTTP helpers ───────────────────────────────────────────────────

  private async request(
    method: string,
    path: string,
    body?: BodyInit | null,
    extraHeaders?: Record<string, string>,
  ): Promise<Response> {
    const headers: Record<string, string> = {
      'x-api-key': this.apiKey,
      ...extraHeaders,
    };

    const res = await fetch(`${this.baseUrl}${path}`, {
      method,
      headers,
      body,
    });

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`FeedbackHub API error ${res.status}: ${text}`);
    }

    return res;
  }
}
